// components/careers.jsx — Careers page for Enginuity
// Mirrors the structure of the live engn.co.za/careers: hero, open positions,
// "why Enginuity" reasons, and a real application form (name, email, phone,
// position, message, CV upload) submitted through the shared PHP handler
// at public/api/send-form.php — no mail client hand-off required.

'use client';

import React, { useState as useState_c, useRef as useRef_c } from 'react';
import { PageHero, Section, Eyebrow, Headline, Button, Field, Honeypot, sendEnginuityForm, isValidEmail } from './ui';
import { ContactCTA } from './home';
import { useOnNav } from '@/lib/nav';
import { OPEN_ROLES, FORMS_ENABLED } from '@/lib/site-data';

const WHY_ENGINUITY = [
  {
    n: '01', title: 'Real projects, real impact',
    body: 'Contribute to EPCM and technology projects for leading mining houses including Anglo American, Black Chrome Mine and Sibanye-Stillwater.',
  },
  {
    n: '02', title: 'Multi-disciplinary by design',
    body: 'Work alongside civil, mechanical, electrical, process and mining specialists under one roof: the same project room, every discipline.',
  },
  {
    n: '03', title: 'Room to grow',
    body: 'Mentorship from senior engineers and a clear path toward professional registration with ECSA and SACPCMP.',
  },
  {
    n: '04', title: 'A team that sees you',
    body: 'An office-based team in Centurion where your work is visible, valued and built upon, not lost in a queue.',
  },
];

function RoleCard({ role, onApply }) {
  const [hover, setHover] = useState_c(false);
  return (
    <article
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) auto', gap: 32,
        alignItems: 'center', padding: '34px 0', borderTop: '1px solid var(--hair)',
      }}
      className="enq-role-card"
    >
      <div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 14, alignItems: 'baseline', marginBottom: 12 }}>
          <span style={{
            fontFamily: '"Source Sans 3", sans-serif', fontSize: 12, fontWeight: 700,
            letterSpacing: '0.12em', textTransform: 'uppercase', color: 'var(--accent)',
          }}>{role.discipline}</span>
          <span style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
            {role.meta.map((m) => (
              <span key={m} style={{ fontSize: 12.5, color: 'var(--mid)' }}>{m}</span>
            ))}
          </span>
        </div>
        <Headline size="sm" as="h3" style={{ maxWidth: '26ch' }}>{role.title}</Headline>
        <p style={{ margin: '12px 0 0', fontSize: 15, lineHeight: 1.6, color: 'var(--fg2)', maxWidth: '64ch' }}>
          {role.blurb}
        </p>
      </div>
      <div className="enq-role-apply" style={{ justifySelf: 'end' }}>
        <Button onClick={() => onApply(role.title)} variant={hover ? 'solid' : 'outline'} size="md">Apply now</Button>
      </div>
    </article>
  );
}

const EMPTY_APPLICATION = { name: '', email: '', phone: '', position: '', message: '' };

function ApplicationForm({ position, setPosition }) {
  const [form, setForm] = useState_c(EMPTY_APPLICATION);
  const [file, setFile] = useState_c(null);
  const [fileError, setFileError] = useState_c('');
  const [errors, setErrors] = useState_c({});
  const [serverError, setServerError] = useState_c('');
  const [sending, setSending] = useState_c(false);
  const [sent, setSent] = useState_c(false);
  const fileInputRef = useRef_c(null);

  const onFileChange = (e) => {
    const f = e.target.files?.[0] || null;
    if (f && f.size > 5 * 1024 * 1024) {
      setFileError('CV must be smaller than 5MB.');
      setFile(null);
      e.target.value = '';
      return;
    }
    setFileError('');
    setFile(f);
  };

  const submit = async (e) => {
    e.preventDefault();
    const nextErrors = {};
    if (!form.name.trim()) nextErrors.name = 'Please enter your name.';
    if (!isValidEmail(form.email)) nextErrors.email = 'Please enter a valid email address.';
    if (!position.trim()) nextErrors.position = 'Please select or enter the role you are applying for.';
    setErrors(nextErrors);
    setServerError('');
    if (Object.keys(nextErrors).length > 0) return;

    setSending(true);
    const result = await sendEnginuityForm({
      formType: 'careers',
      fields: { name: form.name, email: form.email, phone: form.phone, position, message: form.message },
      file,
    });
    setSending(false);
    if (result.ok) {
      setSent(true);
    } else {
      setServerError(result.message);
      if (result.field) setErrors((f) => ({ ...f, [result.field]: result.message }));
    }
  };

  if (sent) {
    return (
      <div style={{ background: 'var(--bg2)', border: '1px solid var(--accent)', padding: '48px 40px', textAlign: 'left' }}>
        <Eyebrow>Application received</Eyebrow>
        <Headline size="md" as="h2" style={{ marginTop: 16 }}>
          Thanks{form.name ? `, ${form.name.split(' ')[0]}` : ''}. We will be in touch.
        </Headline>
        <p style={{ marginTop: 14, fontSize: 15, lineHeight: 1.6, color: 'var(--fg2)', maxWidth: '48ch' }}>
          We review every application and reach out if there's a fit.
        </p>
        <div style={{ marginTop: 24 }}>
          <Button onClick={() => { setSent(false); setForm(EMPTY_APPLICATION); setFile(null); setPosition(''); setErrors({}); setServerError(''); }} variant="outline">
            Send another application
          </Button>
        </div>
      </div>
    );
  }

  return (
    <form onSubmit={submit} style={{ background: 'var(--bg2)', border: '1px solid var(--hair)', padding: '40px 40px 44px', textAlign: 'left' }}>
      <Honeypot />
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 18 }}>
        <Field label="Your name" required name="name" value={form.name} error={errors.name}
               onChange={(v) => setForm((f) => ({ ...f, name: v }))} />
        <Field label="Phone" name="phone" value={form.phone}
               onChange={(v) => setForm((f) => ({ ...f, phone: v }))} />
      </div>
      <div style={{ marginTop: 18 }}>
        <Field label="Email" required name="email" type="email" value={form.email} error={errors.email}
               onChange={(v) => setForm((f) => ({ ...f, email: v }))} />
      </div>
      <div style={{ marginTop: 18 }}>
        <Field label="Position" required name="position" value={position} error={errors.position}
               onChange={setPosition} />
      </div>
      <div style={{ marginTop: 18 }}>
        <Field label="Message (optional)" name="message" value={form.message} multiline
               onChange={(v) => setForm((f) => ({ ...f, message: v }))} />
      </div>
      <div style={{ marginTop: 18 }}>
        <span style={{ fontSize: 13, color: 'var(--mid)', fontWeight: 600 }}>CV (optional, PDF or Word, max 5MB)</span>
        <div style={{ marginTop: 8, display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
          <Button type="button" onClick={() => fileInputRef.current?.click()} variant="outline" size="sm">
            {file ? 'Change file' : 'Choose file'}
          </Button>
          <span style={{ fontSize: 13.5, color: 'var(--fg2)' }}>{file ? file.name : 'No file chosen'}</span>
          <input ref={fileInputRef} type="file" name="cv" accept=".pdf,.doc,.docx" onChange={onFileChange} style={{ display: 'none' }} />
        </div>
        {fileError && <span style={{ display: 'block', marginTop: 6, fontSize: 13, color: '#b8462f' }}>{fileError}</span>}
      </div>
      {serverError && <p style={{ marginTop: 18, fontSize: 14, color: '#b8462f' }}>{serverError}</p>}
      <div style={{ marginTop: 28, display: 'flex', justifyContent: 'center' }}>
        <Button type="submit" onClick={submit} variant="solid" size="lg" style={{ opacity: sending ? 0.6 : 1, pointerEvents: sending ? 'none' : 'auto' }}>
          {sending ? 'Sending...' : 'Submit application'}
        </Button>
      </div>
    </form>
  );
}

function CareersPage() {
  const onNav = useOnNav();
  const [position, setPosition] = useState_c('');

  const scrollToApply = (title) => {
    if (title !== undefined) setPosition(title);
    const el = document.getElementById('careers-apply');
    if (el) window.scrollTo({ top: el.getBoundingClientRect().top + window.scrollY - 90, behavior: 'smooth' });
  };

  return (
    <>
      <PageHero
        eyebrow="Careers at Enginuity"
        title="Engineer a better tomorrow."
        sub="We're a multi-disciplinary engineering and EPCM partner working on the projects that move industry forward. Explore our open roles in Centurion, or simply introduce yourself and send us your CV."
        tone="industrial"
        img="/assets/careers-hero.webp"
        shift
      >
        <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', marginTop: 32 }}>
          <Button onClick={() => { const el = document.getElementById('careers-roles'); if (el) window.scrollTo({ top: el.getBoundingClientRect().top + window.scrollY - 90, behavior: 'smooth' }); }} variant="solid" size="lg">View open positions</Button>
          <Button onClick={() => scrollToApply('')} variant="light" size="lg">Send us your CV</Button>
        </div>
      </PageHero>

      {/* Open positions */}
      <Section id="careers-roles">
        <div style={{ maxWidth: '64ch' }}>
          <Eyebrow>Open positions</Eyebrow>
          <Headline size="lg" as="h2" style={{ marginTop: 20, maxWidth: '18ch' }}>Current opportunities.</Headline>
          <p style={{ margin: '20px 0 0', fontSize: 17, lineHeight: 1.65, color: 'var(--fg2)', maxWidth: '58ch' }}>
            All roles are full-time and office-based at our Centurion office. Not seeing your discipline?
            We still want to hear from you.
          </p>
        </div>

        <div style={{ marginTop: 40, borderBottom: '1px solid var(--hair)' }}>
          {OPEN_ROLES.map((role) => (
            <RoleCard key={role.id} role={role} onApply={scrollToApply} />
          ))}
        </div>
      </Section>

      {/* Why Enginuity */}
      <section style={{ background: 'var(--bg2)' }}>
        <Section>
          <div style={{ maxWidth: '64ch' }}>
            <Eyebrow>Why Enginuity</Eyebrow>
            <Headline size="lg" as="h2" style={{ marginTop: 20, maxWidth: '24ch' }}>
              Work that bridges real-world experience and cutting-edge innovation.
            </Headline>
          </div>
          <div style={{
            marginTop: 48, display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
            gap: 'clamp(28px, 3vw, 48px)',
          }} className="enq-why-grid">
            {WHY_ENGINUITY.map((r) => (
              <div key={r.n} style={{ display: 'flex', gap: 22, alignItems: 'flex-start' }}>
                <span style={{
                  fontFamily: 'var(--ff-display)', fontWeight: 'var(--display-w)',
                  letterSpacing: 'var(--display-track)', fontSize: 34, lineHeight: 1,
                  color: 'var(--accent)', flex: '0 0 auto',
                }}>{r.n}</span>
                <div>
                  <Headline size="sm" as="h3">{r.title}</Headline>
                  <p style={{ margin: '10px 0 0', fontSize: 15, lineHeight: 1.6, color: 'var(--fg2)', maxWidth: '40ch' }}>{r.body}</p>
                </div>
              </div>
            ))}
          </div>
        </Section>
      </section>

      {/* Apply */}
      <Section id="careers-apply">
        <div style={{ maxWidth: '640px', margin: '0 auto' }}>
          <div style={{ textAlign: 'center' }}>
            <Eyebrow style={{ display: 'inline-flex' }}>Apply</Eyebrow>
            <Headline size="lg" as="h2" style={{ marginTop: 18 }}>Apply online.</Headline>
            <p style={{ margin: '18px auto 0', fontSize: 17, lineHeight: 1.65, color: 'var(--fg2)', maxWidth: '52ch' }}>
              Applying for a listed role or making an open application, tell us a bit about yourself below.
              We review every application and reach out if there's a fit.
            </p>
          </div>
          {/* Application form hidden for now, see FORMS_ENABLED (lib/site-data.js).
              ApplicationForm below is untouched and fully working — flip that
              flag to bring it back. */}
          {FORMS_ENABLED ? (
            <>
              <div style={{ marginTop: 32 }}>
                <ApplicationForm position={position} setPosition={setPosition} />
              </div>
              <p style={{ margin: '20px auto 0', fontSize: 13.5, lineHeight: 1.55, color: 'var(--mid)', maxWidth: '44ch', textAlign: 'center' }}>
                Prefer email? Write to{' '}
                <a href="mailto:careers@engn.co.za" style={{ color: 'var(--fg)', fontWeight: 600 }}>careers@engn.co.za</a> instead.
              </p>
            </>
          ) : (
            <div style={{
              marginTop: 32, background: 'var(--bg2)', border: '1px solid var(--hair)',
              padding: '40px 40px 44px', textAlign: 'center',
            }}>
              <p style={{ margin: '0 0 24px', fontSize: 16, lineHeight: 1.6, color: 'var(--fg2)' }}>
                Our online application form is temporarily unavailable. Email your CV and a short cover
                letter, and we'll review it and reach out if there's a fit.
              </p>
              <a href="mailto:careers@engn.co.za" style={{
                display: 'inline-flex', alignItems: 'center', gap: 12, textDecoration: 'none',
                border: '1px solid var(--accent)', background: 'var(--accent)', color: 'var(--on-accent)',
                padding: '15px 26px', fontFamily: '"Source Sans 3", sans-serif', fontSize: 16, fontWeight: 600,
              }}>
                Email careers@engn.co.za
              </a>
            </div>
          )}
        </div>
      </Section>

      <ContactCTA onNav={onNav} />
    </>
  );
}

export { CareersPage };
